// Web Vitals tracking — Phase 18.
//
// Coleta CLS, INP, LCP, TTFB do client e envia como breadcrumbs ao Sentry
// (categoria `web-vitals`). NAO envia eventos separados para o Sentry para
// nao explodir cota — breadcrumbs ficam anexados ao proximo evento de erro.
//
// Why nao FCP/FID:
//   - FCP e dominado por LCP em 95% dos casos (LCP >= FCP por definicao);
//     LCP isolado ja captura sinal suficiente.
//   - FID foi DEPRECATED pela Web Vitals lib em favor de INP (que e
//     `Interaction to Next Paint` — captura latencia de qualquer interacao,
//     nao so a primeira). Manter so INP.
//
// Why fire-and-forget:
//   - Web Vitals dispatch ocorre apos a render principal — bloquear em
//     erro de rede ao Sentry seria pessimo. Erros dentro de send() sao
//     swallow-and-log.
//
// Phase 18: introducao + hookup em root.tsx. Volume real medido apos primeiro
// release; se cota Sentry pressionar, considerar degrade para 1% sample.

import { addBreadcrumb } from './sentry-setup';

import { logger } from '@/shared/lib/logger';

interface MetricLike {
  name: string;
  value: number;
  rating?: 'good' | 'needs-improvement' | 'poor';
  delta?: number;
  id?: string;
  navigationType?: string;
}

function send(metric: MetricLike): void {
  try {
    addBreadcrumb({
      category: 'web-vitals',
      message: metric.name,
      level: metric.rating === 'poor' ? 'warning' : 'info',
      data: {
        value: round(metric.value),
        ...(metric.rating ? { rating: metric.rating } : {}),
        ...(metric.delta !== undefined ? { delta: round(metric.delta) } : {}),
        ...(metric.id ? { id: metric.id } : {}),
        ...(metric.navigationType ? { nav_type: metric.navigationType } : {}),
      },
    });
    logger.app.debug('[VITALS]', metric.name, metric.value, metric.rating);
  } catch (err: unknown) {
    // Erro em send() NUNCA deve quebrar a app — observability e best-effort.
    logger.app.warn('[VITALS] falha ao enviar metric', err);
  }
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/**
 * Inicia coleta de Web Vitals. Chamado uma unica vez no boot
 * (em `src/app/root.tsx`) apos `createRoot`.
 *
 * Lazy import da lib `web-vitals` — ~3KB gzip, mas so vale a pena baixar
 * se vamos enviar ao Sentry. Sem DSN, `addBreadcrumb` ja e no-op, mas
 * ainda baixamos para permitir o `logger.debug` em dev.
 */
export async function trackWebVitals(): Promise<void> {
  try {
    const { onCLS, onINP, onLCP, onTTFB } = await import('web-vitals');
    onCLS(send);
    onINP(send);
    onLCP(send);
    onTTFB(send);
    logger.app.debug('[VITALS] tracking ativo');
  } catch (err: unknown) {
    logger.app.warn('[VITALS] falha ao iniciar tracking', err);
  }
}
